import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useDashboard from '../../hooks/useDashboard';
import DashboardGrid from './DashboardGrid';
import { getWidgetComponent } from './widgetRegistry';
import CreateBookingModal from '../Bookings/CreateBookingsModal/CreateBookingModal';
import { BookingProvider } from '../../context/bookingContext';

// The dashboard only ever greets with morning/day/evening. Late-night and
// pre-dawn hours fall back to "Guten Abend" rather than a "Gute Nacht" farewell.
const greeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return 'Guten Morgen';
  if (h >= 11 && h < 18) return 'Guten Tag';
  return 'Guten Abend';
};

// Derives the ordered visible list + hidden list from the manifest and the
// user's saved/edited layout. Unknown ids are ignored; widgets not yet in the
// saved order are appended in manifest (registry) order so new widgets surface.
const deriveLayout = (manifestWidgets, order, hidden) => {
  const allowed = new Map(manifestWidgets.map(w => [w.id, w]));
  const hiddenSet = new Set(hidden);
  const seen = new Set();
  const visible = [];
  for (const id of order) {
    const w = allowed.get(id);
    if (w && !hiddenSet.has(id) && !seen.has(id)) {
      visible.push(w);
      seen.add(id);
    }
  }
  for (const w of manifestWidgets) {
    if (!hiddenSet.has(w.id) && !seen.has(w.id)) {
      visible.push(w);
      seen.add(w.id);
    }
  }
  const hiddenWidgets = manifestWidgets.filter(w => hiddenSet.has(w.id));
  return { visible, hiddenWidgets };
};

export default function DashboardView() {
  const { getManifest, saveLayout } = useDashboard();
  const { checkRight, currentUser } = useAuth();
  const firstName = currentUser?.userName ? currentUser.userName.trim().split(/\s+/)[0] : '';

  const [manifestWidgets, setManifestWidgets] = useState([]);
  const [order, setOrder] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const dirtyRef = useRef(false);

  const canCreateBooking = checkRight('internalBooking:create');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getManifest();
        if (cancelled) return;
        setManifestWidgets(res?.widgets || []);
        setOrder(res?.layout?.order || []);
        setHidden(res?.layout?.hidden || []);
      } catch {
        if (!cancelled) toast.error('Fehler beim Laden des Dashboards');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getManifest]);

  const { visible, hiddenWidgets } = useMemo(
    () => deriveLayout(manifestWidgets, order, hidden),
    [manifestWidgets, order, hidden]
  );

  const persist = useCallback(
    async (nextOrder, nextHidden) => {
      try {
        await saveLayout({ order: nextOrder, hidden: nextHidden });
        dirtyRef.current = false;
      } catch {
        toast.error('Dashboard-Layout konnte nicht gespeichert werden');
      }
    },
    [saveLayout]
  );

  const handleReorder = useCallback(
    nextIds => {
      setOrder(nextIds);
      dirtyRef.current = true;
    },
    []
  );

  const handleHide = useCallback(
    id => {
      setHidden(prev => (prev.includes(id) ? prev : [...prev, id]));
      setOrder(prev => prev.filter(x => x !== id));
      dirtyRef.current = true;
    },
    []
  );

  const handleShow = useCallback(
    id => {
      setHidden(prev => prev.filter(x => x !== id));
      setOrder(prev => (prev.includes(id) ? prev : [...prev, id]));
      dirtyRef.current = true;
    },
    []
  );

  const handleReset = useCallback(() => {
    setHidden([]);
    setOrder(manifestWidgets.map(w => w.id));
    dirtyRef.current = true;
  }, [manifestWidgets]);

  const toggleEdit = useCallback(() => {
    setEditMode(prev => {
      const next = !prev;
      // Persist the current ordering of *visible* widgets when leaving edit mode.
      if (prev && dirtyRef.current) {
        persist(visible.map(w => w.id), hidden);
      }
      return next;
    });
  }, [persist, visible, hidden]);

  const renderWidget = useCallback(
    (widget, { dragHandleProps }) => {
      const Component = getWidgetComponent(widget);
      if (!Component) return null;
      return (
        <Component
          widget={widget}
          editMode={editMode}
          dragHandleProps={dragHandleProps}
          onHide={() => handleHide(widget.id)}
        />
      );
    },
    [editMode, handleHide]
  );

  if (loading) {
    return (
      <Stack alignItems='center' justifyContent='center' sx={{ minHeight: '40vh' }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header: greeting + quick actions */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent='space-between'
        spacing={1.5}
        sx={{ mb: { xs: 2, sm: 2.5 } }}
      >
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {greeting()}
            {firstName ? `, ${firstName}` : ''} 👋
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {new Date().toLocaleDateString('de-DE', {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </Typography>
        </Box>

        {/* On mobile the actions stack vertically and share the full width so
            both buttons line up at the same width; on larger screens they sit
            inline at their natural width. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          flexWrap='wrap'
          useFlexGap
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {canCreateBooking && (
            <Button
              variant='contained'
              startIcon={<AddCircleIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buchung erstellen
            </Button>
          )}
          <Button
            variant={editMode ? 'contained' : 'outlined'}
            color={editMode ? 'success' : 'primary'}
            startIcon={editMode ? <CheckIcon /> : <TuneIcon />}
            onClick={toggleEdit}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {editMode ? 'Fertig' : 'Anpassen'}
          </Button>
        </Stack>
      </Stack>

      {/* Edit-mode helper bar: hidden widgets + reset */}
      {editMode && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: hiddenWidgets.length ? 1 : 0 }} flexWrap='wrap'>
            <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
              Dashboard anpassen
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ flexGrow: 1 }}>
              Ziehe Kacheln zum Sortieren · blende sie über das Augensymbol aus.
            </Typography>
            <Tooltip title='Auf Standard zurücksetzen'>
              <IconButton size='small' onClick={handleReset}>
                <RestartAltIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
          {hiddenWidgets.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.75 }}>
                Ausgeblendet — zum Wiedereinblenden tippen:
              </Typography>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                {hiddenWidgets.map(w => (
                  <Chip
                    key={w.id}
                    label={w.title}
                    size='small'
                    icon={<AddIcon />}
                    onClick={() => handleShow(w.id)}
                    variant='outlined'
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}

      {visible.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 4, textAlign: 'center' }}>
          Keine Widgets aktiv. Tippe auf „Anpassen“, um welche einzublenden.
        </Typography>
      ) : (
        <DashboardGrid items={visible} editMode={editMode} onReorder={handleReorder} renderItem={renderWidget} />
      )}

      {canCreateBooking && createOpen && (
        <BookingProvider>
          <CreateBookingModal open={createOpen} onClose={() => setCreateOpen(false)} />
        </BookingProvider>
      )}
    </Box>
  );
}
